import React, { useState } from "react"
import { useSecureRequest } from "../../../hooks/useSecureRequest"
import { useCompany } from "../../../contexts/CompanyContext"
import { useConfig } from "../../../contexts/ConfigContext"
import { Input, message, Select, DatePicker, TimeRangePickerProps } from "antd"
import { useRegister } from "../../../contexts/RegisterContext"
import { movementStatus } from "../../../config/const"
import dayjs from "dayjs"
import ES from 'antd/es/date-picker/locale/es_ES'

const { Search } = Input
const { RangePicker } = DatePicker

type HeaderElementsProps = {
  setSpinning: (spinning: boolean) => void
}

function HeaderElements({ setSpinning }: Readonly<HeaderElementsProps>) {
  const [loading, setLoading] = useState(false)
  const { company } = useCompany()
  const { config } = useConfig()
  const { register, updateRegister } = useRegister()
  const { securePost } = useSecureRequest()

  // Combine and deduplicate movement types for the selector
  const uniqueMovementTypes = Array.from(
    new Map(
      [...register.movementTypesEntry, ...register.movementTypesExit]
        .filter((type): type is typeof type => Boolean(type?.key))
        .map(type => [type.key, type])
    ).values()
  )

  const handleMovementSearch = async (text: string) => {
    if (!register.currentMovementType) {
      message.warning("Seleccione un tipo de movimiento primero")
      return
    }
    
    setLoading(true)
    const path = text ? '/inventory/movement/search-movements' : '/inventory/movement/get-movements'
    const requestData: any = {
      company: company.current?.uid,
      search: text,
      type: "full",
      documentType: register.currentMovementType,
      status: movementStatus.COMPLETED,
      module: config.module
    }
    
    if (!text && register.datePickerFilter?.[0] && register.datePickerFilter[1]) {
      requestData.filter = {
        startDate: dayjs(register.datePickerFilter[0]).startOf('day').valueOf(),
        endDate: dayjs(register.datePickerFilter[1]).endOf('day').valueOf(),
      }
    }
    
    const itemResponse = await securePost(path, requestData)
    if (itemResponse.status === 200) {
      if (itemResponse.movementList.length) {
        updateRegister({ movementList: itemResponse.movementList })
      } else {
        message.error("No se encontraron resultados")
      }
    }
    setLoading(false)
  }

  const handleMovementTypeChange = async (value: string) => {
    updateRegister({ currentMovementType: value })
    setSpinning(true)
    
    const requestData: any = {
      company: company.current?.uid,
      module: config.module,
      status: movementStatus.COMPLETED,
      documentType: value
    }
    
    if (register.datePickerFilter?.[0] && register.datePickerFilter[1]) {
      requestData.filter = {
        startDate: dayjs(register.datePickerFilter[0]).startOf('day').valueOf(),
        endDate: dayjs(register.datePickerFilter[1]).endOf('day').valueOf(),
      }
    }
    
    const response = await securePost('/inventory/movement/get-movements', requestData)
    setSpinning(false)
    
    if (response.status === 200) {
      updateRegister({ movementList: response.movementList })
    } else {
      message.error("Ha ocurrido un error, por favor recargue la página e intente de nuevo")
    }
  }

  const handleDateChange: TimeRangePickerProps["onChange"] = (dates, dateStrings) => {
    (async () => {
      if (dates && register.currentMovementType) {
        console.log('Consulta por rango: ', dateStrings)
        setSpinning(true)
        
        const response = await securePost('/inventory/movement/get-movements', {
          company: company.current.uid,
          status: movementStatus.COMPLETED,
          documentType: register.currentMovementType,
          module: config.module,
          filter: {
            startDate: dates[0] ? dates[0].startOf('day').valueOf() : undefined,
            endDate: dates[1] ? dates[1].endOf('day').valueOf() : undefined,
          }
        })
        
        setSpinning(false)
        if (response.status === 200) {
          console.log("Movements", response.movementList)
          updateRegister({ movementList: response.movementList, datePickerFilter: dateStrings })
        } else {
          message.error("Ha ocurrido un error, por favor recargue la página e intente de nuevo")
        }
      }
    })()
  }

  const rangePresets: TimeRangePickerProps['presets'] = [
    { label: 'Hoy', value: [dayjs().startOf('day'), dayjs().endOf('day')] },
    { label: 'Últimos 7 días', value: [dayjs().subtract(6, 'day').startOf('day'), dayjs().endOf('day')] },
    { label: 'Últimos 14 días', value: [dayjs().subtract(13, 'day').startOf('day'), dayjs().endOf('day')] },
    { label: 'Últimos 30 días', value: [dayjs().subtract(29, 'day').startOf('day'), dayjs().endOf('day')] },
    { label: 'Últimos 90 días', value: [dayjs().subtract(89, 'day').startOf('day'), dayjs().endOf('day')] },
  ]

  const start = register.datePickerFilter?.[0] ? dayjs(register.datePickerFilter[0]) : dayjs().startOf('day')
  const end = register.datePickerFilter?.[1] ? dayjs(register.datePickerFilter[1]) : dayjs().endOf('day')

  return (
    <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap", justifyContent: "flex-end", width: "100%" }}>
      <Select
        placeholder="Tipo de movimiento"
        value={register.currentMovementType}
        onChange={handleMovementTypeChange}
        style={{ width: 220 }}
        virtual={false}
        options={[
          { label: "Todos los movimientos", value: "ALL" },
          { label: "🟢 Solo Entradas", value: "ENTRIES_ONLY" },
          { label: "🔴 Solo Salidas", value: "EXITS_ONLY" },
          ...uniqueMovementTypes.map(type => ({
            label: type.string_value,
            value: type.key
          }))
        ]}
      />
      <Search
        placeholder="Buscar por término"
        allowClear
        onSearch={handleMovementSearch}
        loading={loading}
        enterButton
        style={{ width: 220 }}
        disabled={!register.currentMovementType}
      />
      <RangePicker
        presets={rangePresets}
        format="YYYY/MM/DD"
        defaultValue={[start, end]}
        locale={ES}
        onChange={handleDateChange}
        disabled={!register.currentMovementType}
      />
    </div>
  )
}

export default HeaderElements
